import { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import { RecurrenceSelector } from '../templates/RecurrenceSelector';
import { getRecurrenceLabel } from '../../utils/recurrenceHelpers';

const db = firebase.firestore();
const functions = firebase.functions();

/**
 * @typedef {Object} TaskTemplate
 * @property {string} [id]
 * @property {string} [title]
 * @property {string} [description]
 * @property {string} [locationId]
 * @property {string} [assigneeId]
 * @property {boolean} [photoRequired]
 * @property {Object} [recurrence]
 * @property {Array} [steps]
 */

/**
 * Task form modal - create or edit task templates
 * Handles recurrence patterns and task configuration
 * 
 * @param {Object} props
 * @param {TaskTemplate} [props.task]
 * @param {Array<{id: string, name: string}>} props.locations
 * @param {Array<{id: string, displayName: string}>} props.users
 * @param {() => void} props.onClose
 * @param {() => void} props.onSuccess
 */
export function TaskFormModal({ task, locations, users, onClose, onSuccess }) {
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [locationId, setLocationId] = useState(task?.locationId || '');
  const [assigneeId, setAssigneeId] = useState(task?.assigneeId || '');
  const [photoRequired, setPhotoRequired] = useState(task?.photoRequired || false);
  const [recurrence, setRecurrence] = useState(task?.recurrence || {
    type: 'weekly',
    interval: 1,
    daysOfWeek: [1], // ✅ Array mit Montag als Default
    startDate: new Date().toISOString().split('T')[0], // ✅ Nur das Datum (YYYY-MM-DD)
    endDate: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Steps state (max 5 steps)
  const [steps, setSteps] = useState(() => {
    if (task?.steps && task.steps.length > 0) {
      return task.steps.map((step, idx) => ({
        id: step.id || `step-${idx + 1}`,
        text: step.text || '',
        order: idx + 1
      }));
    }
    return [{ id: 'step-1', text: '', order: 1 }];
  });
  
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Step management functions
  const handleStepChange = (index, value) => {
    const newSteps = [...steps];
    newSteps[index].text = value;
    setSteps(newSteps);

    // Auto-add next step if current is filled and not at max
    if (value.trim() && index === steps.length - 1 && steps.length < 5) {
      setSteps([...newSteps, {
        id: `step-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
        text: '',
        order: steps.length + 1
      }]);
    }
    
    // Auto-remove empty trailing steps (but keep at least 1)
    if (!value.trim() && index < steps.length - 1) {
      const filtered = newSteps.filter((s, i) => i <= index || s.text.trim());
      if (filtered.length === 0) {
        setSteps([{ id: 'step-1', text: '', order: 1 }]);
      } else {
        setSteps(filtered);
      }
    }
  };
  
  const moveStep = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === steps.length - 1) return;
    
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    
    // Update order
    newSteps.forEach((step, idx) => {
      step.order = idx + 1;
    });
    
    setSteps(newSteps);
  };
  
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!Number.isFinite(dragIndex) || dragIndex < 0 || dragIndex >= steps.length) return;
    if (dragIndex === dropIndex) return;
    
    const withoutRemoved = steps.filter((_, idx) => idx !== dragIndex);
    const removedItem = steps[dragIndex];
    const newSteps = [
      ...withoutRemoved.slice(0, dropIndex),
      removedItem,
      ...withoutRemoved.slice(dropIndex)
    ];
    
    // Update order
    newSteps.forEach((step, idx) => {
      step.order = idx + 1;
    });
    
    setSteps(newSteps);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Comprehensive validation
    if (!title || !locationId || !assigneeId) {
      setError('Bitte füllen Sie alle Pflichtfelder aus');
      setLoading(false);
      return;
    }

    if (recurrence.type === 'weekly' && (!recurrence.daysOfWeek || recurrence.daysOfWeek.length === 0)) {
      setError('Bitte wählen Sie mindestens einen Wochentag');
      setLoading(false);
      return;
    }

    if (recurrence.type === 'monthly' && !recurrence.monthlyPattern) {
      setError('Bitte konfigurieren Sie die monatliche Wiederholung');
      setLoading(false);
      return;
    }

    // Validate steps - comprehensive frontend validation (saves Cloud Function time)
    const validSteps = steps.filter(s => s.text && s.text.trim()).map((step, idx) => ({
      id: step.id,
      text: step.text.trim(),
      completed: false,
      order: idx + 1
    }));

    // Maximum 5 steps allowed
    if (validSteps.length > 5) {
      setError('Maximal 5 Schritte erlaubt');
      setLoading(false);
      return;
    }

    // Validate each step
    for (let i = 0; i < validSteps.length; i++) {
      const step = validSteps[i];

      // Character limit check
      if (step.text.length > 120) {
        setError(`Schritt ${i + 1} überschreitet 120 Zeichen`);
        setLoading(false);
        return;
      }

      // Empty check (shouldn't happen due to filter, but defensive)
      if (step.text.length === 0) {
        setError(`Schritt ${i + 1} darf nicht leer sein`);
        setLoading(false);
        return;
      }
    }

    try {
      if (task) {
        // Update existing template
        const updateTaskTemplate = functions.httpsCallable('updateTaskTemplate');
        await updateTaskTemplate({
          templateId: task.id,
          updates: {
            title,
            description,
            locationId,
            assigneeId,
            photoRequired,
            recurrence,
            steps: validSteps
          }
        });
      } else {
        // Create new template
        const createTaskTemplate = functions.httpsCallable('createTaskTemplate');
        await createTaskTemplate({
          title,
          description,
          locationId,
          assigneeId,
          photoRequired,
          recurrence,
          steps: validSteps
        });
      }

      onSuccess();
    } catch (err) {
      // console statement removed for production
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}</h2>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Titel *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z.B. Hygienekontrolle"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Detaillierte Beschreibung der Aufgabe..."
                rows={3}
              />
            </div>
          </div>

          <div className="form-row form-row-2">
            <div className="form-group">
              <label>Standort *</label>
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                required
              >
                <option value="">-- Bitte wählen --</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Zuständige(r) *</label>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                required
              >
                <option value="">-- Bitte wählen --</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.displayName}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={photoRequired}
                  onChange={(e) => setPhotoRequired(e.target.checked)}
                />
                <span>Foto beim Abschließen erforderlich</span>
              </label>
            </div>
          </div>

          <div className="form-section">
            <h3>Schritte (optional, max. 5)</h3>
            <p className="form-hint">Definieren Sie Schritte, die bei der Aufgabe abgehakt werden können</p>
            
            <div className="steps-list">
              {steps.map((step, index) => (
                <div 
                  key={step.id} 
                  className="step-input-row"
                  draggable={!isMobile && steps.length > 1}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  {!isMobile && steps.length > 1 && (
                    <div className="drag-handle">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                  )}
                  
                  <div className="step-input-container">
                    <input
                      type="text"
                      value={step.text}
                      onChange={(e) => handleStepChange(index, e.target.value)}
                      placeholder={`Schritt ${index + 1}`}
                      maxLength={120}
                    />
                    <span className="char-counter">
                      {step.text.length}/120
                    </span>
                  </div>
                  
                  {isMobile && steps.length > 1 && (
                    <div className="step-mobile-controls">
                      <button
                        type="button"
                        onClick={() => moveStep(index, 'up')}
                        disabled={index === 0}
                        className="btn-icon"
                      >
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStep(index, 'down')}
                        disabled={index === steps.length - 1}
                        className="btn-icon"
                      >
                        <i className="fas fa-arrow-down"></i>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>Wiederholung konfigurieren</h3>
            <RecurrenceSelector
              recurrence={recurrence}
              onChange={setRecurrence}
            />
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Abbrechen
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Speichert...' : (task ? 'Speichern' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// RESOLVE ISSUES MODAL - Admin can resolve completed-with-issues tasks
// ============================================================================

